import Promise from 'bluebird';
import { useCallback } from 'react';
import { getTaskPoller } from '../task-poller';

function checkError(err) {
  const { errorCode } = err || {};
  return ['GenerateImagesAIGCNoRecommendation', 'AI_Generation_Failed'].includes(errorCode);
}

// retry promise if it fails within timeout
function retryPromise(getPromise, retryCount, start, logger, { timeout = 5000, shouldRetry = () => true }) {
  return getPromise().catch((err) => {
    const now = Date.now();
    const elapsed = now - start;

    if (retryCount >= 1 && elapsed < timeout && shouldRetry(err)) {
      return retryPromise(getPromise, retryCount - 1, start, logger, { timeout, shouldRetry });
    }

    logger.logError('Re-tried but failed to generate image by AI');
    return Promise.reject(err);
  });
}

export const useAIGeneratedImages = ({
  repository,
  logger,
  pollInterval = 3000,
  maxRetryCount = 3, // max retry count for polling task status
}) => {
  const generateImagesByAI = useCallback(({
    prompt, onProgress, onStartPolling, skipBrandDetection,
  }) => {
    if (!repository) {
      return Promise.reject(new Error());
    }

    const generateImagesByAIPromise = () => new Promise((resolve, reject) => {
      // start generating task
      repository.generateImagesWithAIAsync(prompt, skipBrandDetection).then((res) => {
        const { taskId } = res || {};

        if (!taskId && res.brandNameDetected) {
          resolve({
            brandNameDetected: true, brandNames: res.brandNames, prompt, images: [],
          });
          return;
        }

        // poll task status
        let retryCount = 0;
        const pollStatus = () => repository.getAIGeneratedImageTaskStatus(taskId).then((taskStatus) => {
          // status, progress, requestId, images, isTaskDone, taskId,
          const { isTaskDone, ...rest } = taskStatus || {};

          // reset retry count if there is successfull poll
          retryCount = 0;

          // report status
          if (onProgress) {
            onProgress({ ...rest, isTaskDone });
          }

          if (isTaskDone) {
            resolve({ ...rest, prompt });
          }

          // stop polling if it's done
          return { isFinished: isTaskDone };
        }).catch((err) => {
          retryCount += 1;
          if (err.shouldRetry === false || retryCount >= maxRetryCount) { // got continuous errors
            reject(err);
            // stop polling if failed maxRetryCount times continuously
            return { isFinished: true };
          }

          // ignore errors and retry
          logger.logError(`failed to get AI generation task status, retryCount: ${retryCount}, taskId: ${taskId}, prompt: ${prompt}, error: ${err.uxMessage}`);
          return { isFinished: false };
        });

        // start polling task status
        const taskPoller = getTaskPoller({ task: pollStatus, interval: pollInterval });
        taskPoller.start();
        if (onStartPolling) {
          onStartPolling(taskPoller);
        }
      }).catch((err) => {
        reject(err);
      });
    });

    const ret = retryPromise(generateImagesByAIPromise, 1, Date.now(), logger, { shouldRetry: checkError, timeout: 10000 });

    if (logger.monitorPromise) {
      return logger.monitorPromise({ promise: ret, msg: `generateImagesByAI, prompt: ${prompt}`, api: 'generateImagesByAI' });
    }
    return ret;
  }, [logger, maxRetryCount, pollInterval, repository]);

  return {
    generateImagesByAI,
  };
};
